import type { CashFlowMonth, CategoryTotal, OtherCurrencyTotals } from "@/api/types";
import { cn } from "@/lib/utils";
import { shortMonth } from "../../lib/dates";
import { formatMoney } from "../../lib/money";

/**
 * Valores em moedas diferentes da do workspace. A API nunca as soma aos totais principais (#19),
 * então cada moeda aparece à parte, formatada nela mesma.
 */
export function OtherCurrencyTotalsList({ totals }: { totals: OtherCurrencyTotals[] }) {
  if (totals.length === 0) return null;
  return (
    // biome-ignore lint/a11y/useSemanticElements: agrupa valores fora de formulário — <fieldset> é de formulário
    <div role="group" aria-label="Outras moedas" className="mt-4 border-t border-border pt-3 text-sm">
      <p className="m-0 mb-1.5 text-xs font-medium text-muted">Outras moedas (sem conversão)</p>
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {totals.map((t) => (
          <li key={t.currency} aria-label={t.currency}>
            <span className="font-medium">{t.currency}</span>
            <dl className="m-0 mt-0.5 grid grid-cols-3 gap-2">
              {(
                [
                  ["Receitas", t.incomeMinor],
                  ["Despesas", t.expenseMinor],
                  ["Saldo", t.netMinor],
                ] as const
              ).map(([label, value]) => (
                <div key={label} className="min-w-0">
                  <dt className="text-xs text-muted">{label}</dt>
                  <dd
                    className={cn(
                      "m-0 truncate tabular-nums",
                      label === "Saldo" && value < 0 && "text-expense",
                    )}
                  >
                    {formatMoney(value, t.currency)}
                  </dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Uma tabela por outra moeda, só com os meses em que ela teve movimento. */
export function OtherCurrencyCashFlow({ months }: { months: CashFlowMonth[] }) {
  const currencies = [...new Set(months.flatMap((m) => m.otherCurrencies.map((o) => o.currency)))].sort();
  return currencies.map((currency) => (
    <div key={currency} className="mt-6 overflow-x-auto">
      <p className="m-0 mb-1 text-sm font-medium">Em {currency} (sem conversão)</p>
      <table aria-label={`Fluxo de caixa em ${currency}`} className="w-full min-w-md text-sm">
        <thead>
          <tr className="text-left text-muted">
            <th className="py-2 font-medium">Mês</th>
            <th className="py-2 text-right font-medium">Receitas</th>
            <th className="py-2 text-right font-medium">Despesas</th>
            <th className="py-2 text-right font-medium">Saldo</th>
          </tr>
        </thead>
        <tbody>
          {months.flatMap((m) => {
            const t = m.otherCurrencies.find((o) => o.currency === currency);
            if (!t) return [];
            return (
              <tr key={m.month} className="border-t border-border">
                <td className="py-2">{shortMonth(m.month)}</td>
                <td className="py-2 text-right tabular-nums">{formatMoney(t.incomeMinor, currency)}</td>
                <td className="py-2 text-right tabular-nums">{formatMoney(t.expenseMinor, currency)}</td>
                <td
                  className={cn("py-2 text-right font-medium tabular-nums", t.netMinor < 0 && "text-expense")}
                >
                  {formatMoney(t.netMinor, currency)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  ));
}

/** Despesas por categoria de cada outra moeda com despesa no mês. */
export function OtherCurrencyExpenses({
  others,
}: {
  others: Array<OtherCurrencyTotals & { byCategory: CategoryTotal[] }>;
}) {
  return others
    .filter((o) => o.expenseMinor > 0)
    .map((o) => (
      <div key={o.currency} className="mt-6 border-t border-border pt-4">
        <p className="m-0 mb-2 flex justify-between gap-3 text-sm font-medium">
          <span>Em {o.currency} (sem conversão)</span>
          <span className="tabular-nums">{formatMoney(o.expenseMinor, o.currency)}</span>
        </p>
        <ul
          aria-label={`Despesas em ${o.currency}`}
          className="m-0 flex list-none flex-col gap-1.5 p-0 text-sm"
        >
          {o.byCategory
            .filter((c) => c.kind === "expense")
            .map((c) => (
              <li key={c.categoryId} aria-label={c.name} className="flex justify-between gap-3">
                <span className="truncate">{c.name}</span>
                <span className="shrink-0 tabular-nums">{formatMoney(c.totalMinor, o.currency)}</span>
              </li>
            ))}
        </ul>
      </div>
    ));
}
